import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar, X, Search, Check, Save, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { updateBoardMessage, getProperties, getUnits, getTenants, getLeads, getHousekeepers, getMaintenancePersonnel } from '@/lib/api';

const ENTITY_TYPES = [
  { value: 'property', label: 'Property' },
  { value: 'unit', label: 'Unit' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'lead', label: 'Lead' },
  { value: 'housekeeper', label: 'Housekeeper' },
  { value: 'maintenance_personnel', label: 'Maintenance' },
];

function EntityPicker({ entityType, selected, onAdd, onRemove, onDone }) {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSearch('');
    const fetchers = {
      property: () => getProperties(),
      unit: () => getUnits(),
      tenant: () => getTenants(),
      lead: () => getLeads(),
      housekeeper: () => getHousekeepers(),
      maintenance_personnel: () => getMaintenancePersonnel(),
    };
    const fn = fetchers[entityType];
    if (!fn) { setLoading(false); return; }
    fn().then(data => {
      const mapped = data.map(d => {
        let label = d.name || d.title || d.unit_number || d.id;
        if (entityType === 'unit') label = `${d.property_name || ''} ${d.unit_number || ''}`.trim() || d.id;
        return { id: d.id, label };
      });
      setOptions(mapped);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [entityType]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selectedIds = new Set(selected.map(s => s.id));

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <Input className="pl-8 h-8 text-[13px]" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {loading ? (
        <p className="text-xs text-gray-400 py-2">Loading...</p>
      ) : (
        <div className="max-h-[120px] overflow-y-auto border rounded-md">
          {filtered.length === 0 ? (
            <p className="text-xs text-gray-400 p-2">No results</p>
          ) : filtered.map(opt => (
            <button
              key={opt.id}
              className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-50 flex items-center justify-between border-b last:border-0 ${selectedIds.has(opt.id) ? 'bg-teal-50 text-teal-700' : 'text-gray-700'}`}
              onClick={() => {
                if (selectedIds.has(opt.id)) { onRemove(opt.id); }
                else { onAdd({ type: entityType, id: opt.id, label: opt.label }); onDone(); }
              }}
            >
              <span className="truncate">{opt.label}</span>
              {selectedIds.has(opt.id) && <Check className="h-3 w-3 text-teal-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function EditMessageDialog({ message, open, onOpenChange, onUpdated }) {
  const [content, setContent] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [linkedEntities, setLinkedEntities] = useState([]);
  const [activeEntityType, setActiveEntityType] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && message) {
      setContent(message.content || '');
      setDueDate(message.due_date || '');
      setLinkedEntities(message.linked_entities || []);
      setActiveEntityType('');
    }
  }, [open, message]);

  if (!message) return null;

  const addEntity = (entity) => setLinkedEntities(prev => [...prev, entity]);
  const removeEntity = (id) => setLinkedEntities(prev => prev.filter(e => e.id !== id));

  const handleSave = async () => {
    if (!content.trim()) { toast.error('Message cannot be empty'); return; }
    setSaving(true);
    try {
      await updateBoardMessage(message.id, {
        content: content.trim(),
        due_date: dueDate || null,
        linked_entities: linkedEntities,
      });
      toast.success('Message updated');
      onOpenChange(false);
      onUpdated?.();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="edit-message-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-semibold">Edit Message</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-[13px] font-medium">Message</Label>
            <Textarea value={content} onChange={e => setContent(e.target.value)} className="min-h-[100px] text-[13px]" data-testid="edit-msg-content" />
          </div>
          <div className="space-y-2">
            <Label className="text-[13px] font-medium">Due Date</Label>
            <div className="relative">
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input type="date" className="pl-9 h-9 text-[13px]" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="edit-msg-due-date" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[13px] font-medium flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5 text-gray-400" /> Attached Items
            </Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {ENTITY_TYPES.map(et => (
                <button
                  key={et.value}
                  onClick={() => setActiveEntityType(activeEntityType === et.value ? '' : et.value)}
                  className={`px-2.5 py-1 rounded-md text-[12px] font-medium border transition-colors ${
                    activeEntityType === et.value ? 'bg-teal-50 border-teal-300 text-teal-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {et.label}
                </button>
              ))}
            </div>
            {activeEntityType && (
              <EntityPicker
                entityType={activeEntityType}
                selected={linkedEntities.filter(e => e.type === activeEntityType)}
                onAdd={addEntity}
                onRemove={removeEntity}
                onDone={() => setActiveEntityType('')}
              />
            )}
            {linkedEntities.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                {linkedEntities.map(e => (
                  <Badge key={`${e.type}-${e.id}`} variant="secondary" className="text-[11px] gap-1 pr-1">
                    <span className="text-gray-400 capitalize">{e.type}:</span> {e.label}
                    <button onClick={() => removeEntity(e.id)} className="ml-0.5 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="edit-msg-save">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
